import pluralize from 'pluralize';
import { memo } from 'react';

import { ArrowUpRightIcon, ScanSearchIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

import { WorkspaceAgentAssetListRows } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetListRows';
import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';

export type WorkspaceAgentAssetListHoverPanelProps = {
  onOpenList?: () => void;
  onOpenThumbnail?: (id: string) => void;
  thumbnails: WorkspaceAgentThumbnail[];
  title?: string;
  /** Members the workspace can no longer return; `0` is a real answer, not a missing one. */
  unavailableCount: number;
};

/** Scrollable asset list shown inside the list-pill hover card or scope popover. */
export const WorkspaceAgentAssetListHoverPanel = memo(
  ({ onOpenList, onOpenThumbnail, thumbnails, title, unavailableCount }: WorkspaceAgentAssetListHoverPanelProps) => {
    const totalLabel = pluralize('asset', thumbnails.length, true);
    const footerLabel = unavailableCount > 0 ? `${totalLabel} · ${unavailableCount} unavailable` : null;

    return (
      <div
        className="w-[min(280px,calc(100vw-2rem))] overflow-hidden"
        data-testid="WORKSPACE_AGENT_ASSET_LIST_HOVER_PANEL"
      >
        {title || onOpenList ? (
          <div className="flex shrink-0 items-center gap-2 ads:border-default border-gray-4 border-b px-2 py-1.5">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-6 ads:bg-subtle bg-gray-3 ads:text-icon-default text-gray-11">
              <ScanSearchIcon aria-hidden size="xs" />
            </span>
            <span className="min-w-0 flex-1 truncate font-medium ads:text-primary text-12 text-gray-12">
              {title ?? totalLabel}
            </span>
            {onOpenList ? (
              <IconButton
                appearance="ghost"
                color="gray"
                data-testid="WORKSPACE_AGENT_ASSET_LIST_HOVER_OPEN_BUTTON"
                label="Open"
                onClick={onOpenList}
                size="small"
              >
                <ArrowUpRightIcon aria-hidden size="xs" />
              </IconButton>
            ) : null}
          </div>
        ) : null}
        <WorkspaceAgentAssetListRows onOpen={onOpenThumbnail} thumbnails={thumbnails} />
        {footerLabel ? (
          <p className="ads:border-default border-gray-5 border-t px-3 py-1.5 ads:text-metadata text-12 text-gray-10">
            {footerLabel}
          </p>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentAssetListHoverPanel.displayName = 'WorkspaceAgentAssetListHoverPanel';
